import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useLang } from "@/context/LangContext";

const PRODUCT_LINEUP = "https://customer-assets-39nsmqrw.emergentagent.net/job_pakla-wash/artifacts/n9vfugs6_pakla_product_visual.webp";

const Products = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section
      id="product"
      ref={ref}
      data-testid="products-section"
      className="relative py-24 md:py-32 lg:py-40 overflow-hidden"
    >
      <div className="absolute inset-0 spotlight opacity-70 pointer-events-none" />
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 relative">
        <div className="text-center max-w-4xl mx-auto mb-16 md:mb-20">
          <div className="font-eyebrow text-2xl md:text-[30px] text-[#0F3D2E]/85 mb-6">{t.products.eyebrow}</div>
          <h2 className="font-products text-[#0F3D2E] leading-[0.95] text-[10vw] sm:text-[8vw] md:text-[5.6vw] lg:text-[4.4vw] font-medium">
            {t.products.title.split("\n").map((l, i) => (
              <span key={i} className="block">
                {i === 1 ? <em className="italic text-[#2E8B4A]">{l}</em> : l}
              </span>
            ))}
          </h2>
          <div className="hairline my-8 mx-auto max-w-xs" />
          <p className="text-lg md:text-2xl leading-relaxed text-[#0A1F17]/80 max-w-2xl mx-auto font-serif-editorial italic">
            {t.products.body}
          </p>
        </div>

        <div className="grid grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Left: hero product photo */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 md:col-span-7 relative rounded-2xl overflow-hidden shadow-[0_40px_100px_-30px_rgba(15,61,46,0.35)]"
            data-testid="product-hero-image"
          >
            <motion.img
              style={{ y: imgY }}
              src={PRODUCT_LINEUP}
              alt="PAKLA 1LT and 5LT product line-up with fresh vegetables"
              className="w-full h-auto object-cover"
              draggable="false"
            />
          </motion.div>

          {/* Right: size selectors */}
          <div className="col-span-12 md:col-span-5 flex flex-col gap-4">
            {/* 1L card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="relative bg-[#F2EEE3] rounded-2xl p-8 md:p-10"
              data-testid="product-1l"
            >
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#2E8B4A] mb-3">Home</div>
              <h3 className="font-products text-[#0F3D2E] text-4xl md:text-[42px] leading-tight mb-3 font-semibold">
                {t.products.one.name}
              </h3>
              <p className="text-[15px] leading-relaxed text-[#0A1F17]/80 mb-6">
                {t.products.one.desc}
              </p>
              <a
                href="https://paklashop.myikas.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-1l-cta"
                className="inline-flex self-start items-center gap-2 h-11 px-5 rounded-full bg-[#0F3D2E] text-[#FAF7F0] text-[12px] tracking-[0.2em] uppercase hover:bg-[#2E8B4A] transition-colors"
              >
                {t.products.one.cta}
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6h8m0 0L7 3m3 3L7 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </motion.div>

            {/* 5L card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="relative bg-[#0F3D2E] text-[#FAF7F0] rounded-2xl p-8 md:p-10"
              data-testid="product-5l"
            >
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#2E8B4A] mb-3">Pro</div>
              <h3 className="font-products text-4xl md:text-[42px] leading-tight mb-3 font-semibold">
                {t.products.five.name}
              </h3>
              <p className="text-[15px] leading-relaxed text-[#FAF7F0]/80 mb-6">
                {t.products.five.desc}
              </p>
              <a
                href="https://paklashop.myikas.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-5l-cta"
                className="inline-flex self-start items-center gap-2 h-11 px-5 rounded-full bg-[#FAF7F0] text-[#0F3D2E] text-[12px] tracking-[0.2em] uppercase hover:bg-[#2E8B4A] hover:text-[#FAF7F0] transition-colors"
              >
                {t.products.five.cta}
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6h8m0 0L7 3m3 3L7 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Products;
